import { isNotNil, pick, pickBy, zipWith } from "es-toolkit";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import i18n from "@/modules/common/i18n";
import { formatList } from "@/modules/common/i18n/listFormat";
import ApiService from "../../common/InvApiService";
import { showToastWhilstPending } from "../../util/alerts";
import { getErrorMessage } from "../../util/error";
import { filenameExceptExtension } from "../../util/files";
import * as Parsers from "../../util/parsers";
import { parseString } from "../../util/parsers";
import Result from "../../util/result";
import RsSet from "../../util/set";
import StateMachine from "../../util/stateMachine";
import { match } from "../../util/Util";
import { mkAlert } from "../contexts/Alert";
import type { GlobalId } from "../definitions/BaseRecord";
import type { FieldModelAttrs as TemplateField } from "../models/FieldModel";
import getRootStore from "../stores/getRootStore";
import type { ImportRecordType } from "../stores/ImportStore";
import MemoisedFactory from "./Factory/MemoisedFactory";
import {
  apiStringToFieldType,
  compatibleFieldTypes,
  type FieldType,
  FieldTypes,
  fieldTypeToApiString,
} from "./FieldTypes";
import type InstrumentTemplateModel from "./InstrumentTemplateModel";
import type TemplateModel from "./TemplateModel";
import type { TemplateAttrs } from "./TemplateModel";

type InstrumentTemplateAttrs = {
  name: string;
  fields: Array<{ name: string; type: string; [key: string]: unknown }>;
  [key: string]: unknown;
};

export const Fields: { [fieldName: string]: symbol } = {
  name: Symbol.for("NAME"),
  description: Symbol.for("DESCRIPTION"),
  expiry_date: Symbol.for("EXPIRY DATE"),
  quantity: Symbol.for("QUANTITY"),
  source: Symbol.for("SOURCE"),
  tags: Symbol.for("TAGS"),
  import_identifier: Symbol.for("IMPORT ID"),
  parent_container_global_id: Symbol.for("PARENT CONTAINER GLOBAL ID"),
  parent_container_import_id: Symbol.for("PARENT CONTAINER IMPORT ID"),
  parent_sample_global_id: Symbol.for("PARENT SAMPLE GLOBAL ID"),
  parent_sample_import_id: Symbol.for("PARENT SAMPLE IMPORT ID"),
  identifier: Symbol.for("IDENTIFIER"),
  custom: Symbol.for("CUSTOM"),
  none: Symbol.for("IGNORE"),
};

const IMPORT_RECORD_TYPE_LABEL_KEYS = {
  CONTAINERS: "inventory:recordTypes.container.plural",
  SAMPLES: "inventory:recordTypes.sample.plural",
  SUBSAMPLES: "inventory:recordTypes.subsample.plural",
  INSTRUMENTS: "inventory:recordTypes.instrument.plural",
} as const;

const IMPORT_RESULT_TYPE_LABEL_KEYS = {
  CONTAINER: "inventory:recordTypes.container.plural",
  SAMPLE: "inventory:recordTypes.sample.plural",
  SUBSAMPLE: "inventory:recordTypes.subsample.plural",
  INSTRUMENT: "inventory:recordTypes.instrument.plural",
} as const;

const importRecordTypeLabel = (recordType: ImportRecordType): string =>
  i18n.t(IMPORT_RECORD_TYPE_LABEL_KEYS[recordType]);

const importResultTypeLabel = (recordType: string): string => {
  const key = IMPORT_RESULT_TYPE_LABEL_KEYS[recordType as keyof typeof IMPORT_RESULT_TYPE_LABEL_KEYS];
  return key ? i18n.t(key) : recordType;
};

type Field = (typeof Fields)[keyof typeof Fields];

// returns null for custom and none
export const getTypeOfField: (field: Field) => FieldType | null = match([
  [(f) => f === Fields.name, FieldTypes.plain_text],
  [(f) => f === Fields.description, FieldTypes.formatted_text],
  [(f) => f === Fields.expiry_date, FieldTypes.date],
  [(f) => f === Fields.tags, FieldTypes.plain_text],
  [(f) => f === Fields.source, FieldTypes.radio],
  [(f) => f === Fields.quantity, FieldTypes.plain_text],
  [(f) => f === Fields.import_identifier, FieldTypes.plain_text],
  [(f) => f === Fields.parent_container_global_id, FieldTypes.plain_text],
  [(f) => f === Fields.parent_container_import_id, FieldTypes.plain_text],
  [(f) => f === Fields.parent_sample_global_id, FieldTypes.plain_text],
  [(f) => f === Fields.parent_sample_import_id, FieldTypes.plain_text],
  [(f) => f === Fields.identifier, FieldTypes.plain_text],
  [() => true, null],
]);

const transitionMapping = {
  initial: new Set(["parsing", "submitting"]),
  //   clean state, the page has just loaded (or "clear file" button has been pressed)
  parsing: new Set(["parsed", "parsingFailed"]),
  //   selected file is sent to server for parsing; awaiting response
  parsingFailed: new Set(["initial", "parsing"]),
  //   file could not be parsed, user must select a new file
  parsed: new Set(["initial", "nameSelected", "nameRequired", "parsing"]),
  //   file parsed successfully, user must select name field, or can choose a different file
  nameRequired: new Set(["initial", "nameRequiredAndSelected", "parsing"]),
  //   if the user tries to submit without a name mapping then, and only then, do we show an error
  nameRequiredAndSelected: new Set(["initial", "nameRequired", "submitting", "parsing"]),
  /*
   *   all good to submit, unselecting goes back to "required" error state
   * transition to "initial" may occur when already parsed csv file is cleared
   * transition to "nameSelected" may occur when import of multiple groups fails
   */
  nameSelected: new Set(["initial", "submitting", "parsed", "parsing", "nameSelected"]),
  //   all good to submit, unselecting name goes back to parsed where error is not shown
  submitting: new Set(["nameSelected", "successfullySubmitted"]),
  //   server processes the submission
  successfullySubmitted: new Set(["initial", "parsing"]),
  //   and the success state where the user can start over with a new file
};
export type State = keyof typeof transitionMapping;

const invalidFieldNames = [
  "",
  "Description",
  "Expiry Date",
  "Name",
  "Preview Image",
  "Source",
  "Tags",
  // 'Quantity' name is allowed for now, field uses 'Total Quantity'
];

type ColumnName = string;

export class ColumnFieldMap {
  selected: boolean;
  columnName: ColumnName;
  field: Field;
  fieldName: string;
  fieldType: FieldType;
  chosenFieldType: FieldType;
  quantityUnitId: number | null;
  options: Array<string> | null;
  fieldChangeCallback: (oldField: Field, newField: Field) => void;
  isNameUnique: (columnFieldMap: ColumnFieldMap) => boolean;
  recordType: ImportRecordType;
  columnsWithoutBlankValue: Array<ColumnName>;

  constructor({
    selected,
    columnName,
    field,
    fieldName,
    fieldType,
    quantityUnitId,
    options,
    fieldChangeCallback,
    isNameUnique,
    recordType,
    columnsWithoutBlankValue,
  }: {
    selected: boolean;
    columnName: ColumnName;
    field: Field;
    fieldName: string;
    fieldType: FieldType;
    quantityUnitId: number | null;
    options: Array<string> | null;
    fieldChangeCallback: (oldField: Field, newField: Field) => void;
    isNameUnique: (columnFieldMap: ColumnFieldMap) => boolean;
    recordType: ImportRecordType;
    columnsWithoutBlankValue: Array<ColumnName>;
  }) {
    makeObservable(this, {
      selected: observable,
      columnName: observable,
      field: observable,
      fieldName: observable,
      fieldType: observable,
      chosenFieldType: observable,
      quantityUnitId: observable,
      options: observable,
      recordType: observable,
      toggleSelected: action,
      setField: action,
      updateField: action,
      setChosenFieldType: action,
      setFieldName: action,
      allValidTypes: computed,
      validFieldName: computed,
      valid: computed,
      fieldsByRecordType: computed,
    });
    this.selected = selected;
    this.recordType = recordType;
    this.columnName = columnName;
    this.field = field;
    this.fieldName = fieldName;
    this.fieldType = fieldType;
    this.chosenFieldType = fieldType;
    this.options = options;
    this.fieldChangeCallback = fieldChangeCallback;
    this.isNameUnique = isNameUnique;
    this.quantityUnitId = quantityUnitId;
    this.columnsWithoutBlankValue = columnsWithoutBlankValue;
  }

  toggleSelected(value: boolean = !this.selected) {
    this.selected = value;
  }

  setField(newField: Field) {
    this.field = newField;
  }

  updateField(newField: Field) {
    const oldField = this.field;
    this.setField(newField);
    this.fieldChangeCallback(oldField, newField);
  }

  setChosenFieldType(fieldType: FieldType) {
    this.chosenFieldType = fieldType;
  }

  setFieldName(value: string) {
    this.fieldName = value;
  }

  get allValidTypes(): Array<FieldType> {
    return compatibleFieldTypes(this.fieldType);
  }

  // returns false for custom; caller should handle
  typeIsCompatibleWithField(field: Field): boolean {
    const type = getTypeOfField(field);
    if (type === null) return false;
    return (
      // make none selectable (for containers)
      this.allValidTypes.includes(type) || field === Fields.none
    );
  }

  isCompatibleWithField(field: Field): boolean {
    if (field === Fields.source) {
      return (
        this.fieldType === FieldTypes.radio &&
        Boolean(this.options) &&
        new RsSet(this.options).isSubsetOf(new RsSet(["LAB_CREATED", "VENDOR_SUPPLIED", "OTHER"]))
      );
    }
    if (field === Fields.name) {
      return this.columnsWithoutBlankValue.includes(this.columnName) && this.typeIsCompatibleWithField(field);
    }
    return this.typeIsCompatibleWithField(field);
  }

  get validFieldName(): boolean {
    return (
      // apply no restrictions for non-samples fields name validity
      this.recordType !== "SAMPLES" || (!invalidFieldNames.includes(this.fieldName.trim()) && this.isNameUnique(this))
    );
  }

  get valid(): boolean {
    return this.field !== Fields.custom || this.validFieldName;
  }

  get fieldsByRecordType(): { [fieldName: string]: symbol } {
    const exclusions = {
      SAMPLES: new Set(["parent_sample_global_id", "parent_sample_import_id", "none"]),
      CONTAINERS: new Set([
        "expiry_date",
        "quantity",
        "source",
        "parent_sample_global_id",
        "parent_sample_import_id",
        "custom",
      ]),
      SUBSAMPLES: new Set(["expiry_date", "source", "import_identifier", "custom"]),
      INSTRUMENTS: new Set([
        "quantity",
        "source",
        "expiry_date",
        "parent_sample_global_id",
        "parent_sample_import_id",
        "none",
      ]),
    };
    return pickBy(Fields, (_, key) => !exclusions[this.recordType].has(key as string)) as {
      [fieldName: string]: symbol;
    };
  }
}

export default class Import {
  /*
   * The CSV files to import, per recordType.
   * Can be POSTed indivudually or together.
   */
  containersFile: File | null = null;
  samplesFile: File | null = null;
  subSamplesFile: File | null = null;
  instrumentsFile: File | null = null;

  /*
   * When parsing the CSV file, the server may suggest a different field name
   * from that of the CSV's column name (ie when column name would match a default template field name).
   */
  // @ts-expect-error initialised by parsing CSV
  fieldNameForColumnName: { [columnName: string]: string };

  /*
   * Determines if we are creating a new template or importing using an
   * existing one.
   */
  createNewTemplate: boolean;

  /*
   * The name chosen by the user for their new template. Only applicable when
   * `createNewTemplate` is true, otherwise unused.
   */
  templateName: string;

  /*
   * When creating a new template, the server will respond with the definition
   * of a suggested template. The user has the opportunity to then modify this
   * definition in whichever way the UI exposes this object.
   */
  templateInfo: TemplateAttrs | null;

  /*
   * When importing using an existing template, this is the template chosen by
   * the user. Only the `id` is passed to the server and so no modifications
   * should be permitted by the UI.
   */
  template: TemplateModel | null = null;

  /*
   * Instrument template state, analogous to the sample template state above.
   */
  instrumentCreateNewTemplate: boolean;
  instrumentTemplateName: string;
  instrumentTemplateInfo: InstrumentTemplateAttrs | null;
  instrumentTemplate: InstrumentTemplateModel | null = null;

  /*
   * The column-to-field mappings, per recordType.
   * Can be POSTed indivudually or together.
   */
  containersMappings: Array<ColumnFieldMap> = [];
  samplesMappings: Array<ColumnFieldMap> = [];
  subSamplesMappings: Array<ColumnFieldMap> = [];
  instrumentsMappings: Array<ColumnFieldMap> = [];

  /*
   * A Finite State Automata (State Machine) that models the steps of the
   * import process, ensuring that the several steps are performed in order.
   */
  state: StateMachine<string>;

  /*
   * this is required for handling the import process:
   * custom conversions, POST request, results, UI tabs etc.
   */
  recordType: ImportRecordType;

  constructor(recordType: ImportRecordType) {
    makeObservable(this, {
      containersFile: observable,
      samplesFile: observable,
      subSamplesFile: observable,
      instrumentsFile: observable,
      containersMappings: observable,
      samplesMappings: observable,
      subSamplesMappings: observable,
      instrumentsMappings: observable,
      templateName: observable,
      templateInfo: observable,
      template: observable,
      createNewTemplate: observable,
      instrumentCreateNewTemplate: observable,
      instrumentTemplateName: observable,
      instrumentTemplateInfo: observable,
      instrumentTemplate: observable,
      state: observable,
      recordType: observable,
      validateMappings: observable,
      resetMappingsByRecordType: action,
      resetAllMappings: action,
      resetAllLoadedFiles: action,
      setCreateNewTemplate: action,
      setCurrentRecordType: action,
      setTemplateName: action,
      setTemplate: action,
      setInstrumentCreateNewTemplate: action,
      setInstrumentTemplateName: action,
      setInstrumentTemplate: action,
      transformInstrumentTemplateInfoForSubmission: action,
      setFile: action,
      clearFile: action,
      setDefaultUnitId: action,
      toggleSelection: action,
      transformTemplateInfoForSubmission: action,
      updateRecordType: action,
      nameFieldIsSelected: computed,
      quantityFieldIsSelected: computed,
      validTemplateName: computed,
      anyParentSamplesFieldIsSelected: computed,
      unconvertedFieldIsSelected: computed,
      parentContainersImportIdUndefined: computed,
      parentSamplesImportIdUndefined: computed,
      containersSubmittable: computed,
      samplesSubmittable: computed,
      subSamplesSubmittable: computed,
      instrumentsSubmittable: computed,
      validInstrumentTemplateName: computed,
      importSubmittable: computed,
      fileErrorMessage: computed,
      fileByRecordType: computed,
      importMatchesExistingTemplate: computed,
      importInstrumentMatchesExistingTemplate: computed,
      isContainersImport: computed,
      isSamplesImport: computed,
      isSubSamplesImport: computed,
      isInstrumentsImport: computed,
      labelByRecordType: computed,
      mappingsByRecordType: computed,
      someFileSubmitted: computed,
      fileByRecordTypeLoaded: computed,
    });
    this.recordType = recordType;
    this.templateName = i18n.t("inventory:import.fields.defaultTemplateName");
    this.templateInfo = null;
    this.state = new StateMachine(transitionMapping, "initial", (x) => x, null);
    this.createNewTemplate = true;
    this.instrumentCreateNewTemplate = true;
    this.instrumentTemplateName = i18n.t("inventory:import.fields.defaultTemplateName");
    this.instrumentTemplateInfo = null;
  }

  setCurrentRecordType(value: ImportRecordType) {
    this.recordType = value;
  }

  setTemplateName(value: string) {
    this.templateName = value;
  }

  setCreateNewTemplate(value: boolean) {
    this.createNewTemplate = value;
  }

  setTemplate(template: TemplateModel | null) {
    this.template = template;
    if (template) void template.fetchAdditionalInfo();
  }

  setInstrumentCreateNewTemplate(value: boolean) {
    this.instrumentCreateNewTemplate = value;
  }

  setInstrumentTemplateName(value: string) {
    this.instrumentTemplateName = value;
  }

  setInstrumentTemplate(template: InstrumentTemplateModel | null) {
    this.instrumentTemplate = template;
  }

  setFile(file: File | null) {
    if (this.isSamplesImport) {
      this.samplesFile = file;
    } else if (this.isContainersImport) {
      this.containersFile = file;
    } else if (this.isInstrumentsImport) {
      this.instrumentsFile = file;
    } else {
      this.subSamplesFile = file;
    }

    if (this.isSamplesImport && this.samplesFile) {
      this.setTemplateName(
        i18n.t("inventory:import.fields.defaultTemplateNameFromFile", {
          fileName: filenameExceptExtension(this.samplesFile.name),
        }),
      );
    }

    if (this.isInstrumentsImport && this.instrumentsFile) {
      this.setInstrumentTemplateName(
        i18n.t("inventory:import.fields.defaultTemplateNameFromFile", {
          fileName: filenameExceptExtension(this.instrumentsFile.name),
        }),
      );
    }

    getRootStore().uiStore.setDirty(() => {
      getRootStore().uiStore.unsetDirty();
      return Promise.resolve();
    });

    if (file) void this.parseCsvFile();
  }

  resetMappingsByRecordType() {
    if (this.isSamplesImport) {
      this.samplesMappings = [];
    } else if (this.isContainersImport) {
      this.containersMappings = [];
    } else if (this.isInstrumentsImport) {
      this.instrumentsMappings = [];
    } else {
      this.subSamplesMappings = [];
    }
  }

  resetAllMappings() {
    this.containersMappings = [];
    this.samplesMappings = [];
    this.subSamplesMappings = [];
    this.instrumentsMappings = [];
  }

  resetAllLoadedFiles() {
    this.samplesFile = null;
    this.containersFile = null;
    this.subSamplesFile = null;
    this.instrumentsFile = null;
  }

  clearFile() {
    this.setFile(null);
    this.resetMappingsByRecordType();
    if (this.isSamplesImport) {
      this.setTemplateName(i18n.t("inventory:import.fields.defaultTemplateName"));
    } else if (this.isInstrumentsImport) {
      this.setInstrumentTemplateName(i18n.t("inventory:import.fields.defaultTemplateName"));
    }
    this.state.transitionTo("initial");
  }

  setDefaultUnitId(id: number = 3) {
    if (this.templateInfo) {
      this.templateInfo.defaultUnitId = id;
    }
  }

  get isContainersImport(): boolean {
    return this.recordType === "CONTAINERS";
  }

  get isSamplesImport(): boolean {
    return this.recordType === "SAMPLES";
  }

  get isSubSamplesImport(): boolean {
    return this.recordType === "SUBSAMPLES";
  }

  get isInstrumentsImport(): boolean {
    return this.recordType === "INSTRUMENTS";
  }

  get validInstrumentTemplateName(): boolean {
    return Boolean(this.instrumentTemplateName) && this.instrumentTemplateName.length <= 255;
  }

  get instrumentsSubmittable(): boolean {
    if (!this.instrumentsFile) return false;
    const mappingsValid = this.validateMappings(this.instrumentsMappings);
    const templateValid = this.instrumentCreateNewTemplate
      ? Boolean(this.instrumentTemplateInfo) && this.validInstrumentTemplateName
      : Boolean(this.instrumentTemplate) &&
        this.validInstrumentTemplateName &&
        Boolean(this.importInstrumentMatchesExistingTemplate?.matches);
    return mappingsValid && templateValid;
  }

  get nameFieldIsSelected(): boolean {
    return this.mappingsByRecordType.some((m) => m.field === Fields.name && m.selected);
  }

  get quantityFieldIsSelected(): boolean {
    return this.samplesMappings.some((m) => m.field === Fields.quantity && m.selected);
  }

  get validTemplateName(): boolean {
    return Boolean(this.templateName) && this.templateName.length <= 255;
  }

  get anyParentSamplesFieldIsSelected(): boolean {
    return this.subSamplesMappings.some(
      (m) => (m.field === Fields.parent_sample_import_id || m.field === Fields.parent_sample_global_id) && m.selected,
    );
  }

  get unconvertedFieldIsSelected(): boolean {
    return this.mappingsByRecordType.some((m) => m.field === Fields.none && m.selected);
  }

  /**
   * frontend Parent Container check (for all types) and Parent Sample check (for subsamples only)
   * check that containersMappings / samplesMappings exist and have a compatible mapping "Import identifier" selected, otherwise warn user.
   * id values will be cross-checked by the backend
   */

  get parentContainersImportIdUndefined(): boolean {
    return this.mappingsByRecordType.some((m) => m.field === Fields.parent_container_import_id && m.selected)
      ? !this.containersMappings.some((m) => m.field === Fields.import_identifier && m.selected)
      : false;
  }

  get parentSamplesImportIdUndefined(): boolean {
    return this.subSamplesMappings.some((m) => m.field === Fields.parent_sample_import_id && m.selected)
      ? !this.samplesMappings.some((m) => m.field === Fields.import_identifier && m.selected)
      : false;
  }

  /**
   * validation is per type (specific mappings needed)
   * but only for the common checks (relevant to all types).
   * type-specific checks should be in each type submittable computed
   */
  validateMappings(mappings: Array<ColumnFieldMap>): boolean {
    const allMappingsAreValid: boolean = mappings.every((m) => m.valid);

    const fieldNames = mappings.map((m) => m.fieldName);
    const allFieldNamesAreUnique: boolean = new Set(fieldNames).size === fieldNames.length;

    const nameFieldIsSelected: boolean = mappings.some(
      (m) => m.field === Fields.name && m.selected && m.columnsWithoutBlankValue.includes(m.columnName),
    );

    const parentContainersImportIdUndefined: boolean = mappings.some(
      (m) => m.field === Fields.parent_container_import_id && m.selected,
    )
      ? !this.containersMappings.some((m) => m.field === Fields.import_identifier && m.selected)
      : false;

    return allMappingsAreValid && allFieldNamesAreUnique && nameFieldIsSelected && !parentContainersImportIdUndefined;
  }

  get fileByRecordType(): File | null {
    if (this.isSamplesImport) {
      return this.samplesFile;
    }
    if (this.isContainersImport) {
      return this.containersFile;
    }
    if (this.isInstrumentsImport) {
      return this.instrumentsFile;
    }
    return this.subSamplesFile;
  }

  get someFileSubmitted(): boolean {
    return (
      Boolean(this.containersFile) ||
      Boolean(this.samplesFile) ||
      Boolean(this.subSamplesFile) ||
      Boolean(this.instrumentsFile)
    );
  }

  get fileByRecordTypeLoaded(): boolean {
    return this.isContainersImport
      ? Boolean(this.containersFile)
      : this.isSamplesImport
        ? Boolean(this.samplesFile)
        : this.isInstrumentsImport
          ? Boolean(this.instrumentsFile)
          : Boolean(this.subSamplesFile);
  }

  get labelByRecordType(): string {
    return importRecordTypeLabel(this.recordType);
  }

  get mappingsByRecordType(): Array<ColumnFieldMap> {
    if (this.isContainersImport) return this.containersMappings;
    if (this.isSamplesImport) return this.samplesMappings;
    if (this.isInstrumentsImport) return this.instrumentsMappings;
    return this.subSamplesMappings;
  }

  get containersSubmittable(): boolean {
    return this.containersFile ? this.validateMappings(this.containersMappings) : false;
  }

  get samplesSubmittable(): boolean {
    const validNewTemplate: boolean = Boolean(this.templateInfo) && this.validTemplateName;

    const validExistingTemplate: boolean =
      Boolean(this.template) && this.validTemplateName && Boolean(this.importMatchesExistingTemplate?.matches);

    const templateIsValid: boolean = this.createNewTemplate ? validNewTemplate : validExistingTemplate;

    return this.samplesFile ? this.validateMappings(this.samplesMappings) && templateIsValid : false;
  }

  get subSamplesSubmittable(): boolean {
    return this.subSamplesFile
      ? this.validateMappings(this.subSamplesMappings) &&
          this.anyParentSamplesFieldIsSelected &&
          !this.parentSamplesImportIdUndefined
      : false;
  }

  get importSubmittable(): boolean {
    return (
      this.containersSubmittable || this.samplesSubmittable || this.subSamplesSubmittable || this.instrumentsSubmittable
    );
  }

  toggleSelection() {
    const allAlreadySelected = this.mappingsByRecordType.every((m) => m.selected);
    this.mappingsByRecordType.map((m) => m.toggleSelected(!allAlreadySelected));
  }

  async parseCsvFile(): Promise<void> {
    const state = this.state;
    state.transitionTo("parsing", () => ({}));

    const fieldChangeHandler = (oldField: Field, newField: Field) => {
      if (oldField === Fields.name && newField !== Fields.name) {
        state.assertCurrentState(new RsSet(["nameSelected", "nameRequiredAndSelected"]));
        if (state.isCurrentState("nameSelected")) {
          state.transitionTo("parsed");
        } else {
          state.transitionTo("nameRequired");
        }
      }
      if (oldField !== Fields.name && newField === Fields.name) {
        state.assertCurrentState(new RsSet(["parsed", "nameRequired"]));
        if (state.isCurrentState("parsed")) {
          state.transitionTo("nameSelected");
        } else {
          state.transitionTo("nameRequiredAndSelected");
        }
      }
    };

    // matching column names to Fields key names
    const nameToMatch = (name: string) => {
      const nameKey = name
        .substring(name[0] === "_" ? 1 : 0)
        .replace(/ /g, "_")
        .toLowerCase();
      // supporting one alternative, otherwise exact matches
      return nameKey === "import_id" ? nameKey.concat("entifier") : nameKey;
    };

    try {
      const params = new FormData();
      if (!this.someFileSubmitted) {
        throw new Error("No file set");
      } else {
        params.append("recordType", this.recordType);
        params.append("file", this.fileByRecordType as Blob);

        const { data } = await ApiService.post<{
          columnNames: Array<string>;
          columnsWithoutBlankValue: Array<string>;
          fieldNameForColumnName?: Record<string, string>;
          templateInfo?: TemplateAttrs;
          quantityUnitForColumn?: { [column: string]: number };
          radioOptionsForColumn?: { [column: string]: Array<string> };
          fieldMappings?: Record<string, string>;
        }>("/import/parseFile", params);

        if (this.isSamplesImport) {
          if (!(Boolean(data?.fieldNameForColumnName) && typeof data.fieldNameForColumnName === "object"))
            throw new Error("Field name for column name mapping data missing.");

          runInAction(() => {
            this.templateInfo = data.templateInfo as TemplateAttrs;

            this.fieldNameForColumnName = data.fieldNameForColumnName as Record<string, string>;

            this.samplesMappings = (data.templateInfo as TemplateAttrs).fields.map(({ name, type }): ColumnFieldMap => {
              const originalColumnName = Object.keys(data.fieldNameForColumnName as Record<string, string>).find(
                (key) => this.fieldNameForColumnName[key] === name,
              ) as string;

              const newFieldMap = new ColumnFieldMap({
                recordType: this.recordType,
                selected: true,
                columnName: originalColumnName,
                field: Fields.custom,
                fieldName: name as string,
                fieldType: apiStringToFieldType(type),
                quantityUnitId:
                  (data.quantityUnitForColumn as Exclude<undefined, typeof data.quantityUnitForColumn>)[
                    originalColumnName
                  ] ?? null,
                options: (data.radioOptionsForColumn as Exclude<undefined, typeof data.radioOptionsForColumn>)[
                  originalColumnName
                ],
                fieldChangeCallback: fieldChangeHandler,
                isNameUnique: (c) => this.isNameUnique(c),
                columnsWithoutBlankValue: data.columnsWithoutBlankValue,
              });

              // after creation: auto-select conversion in suitable cases
              const fieldsByRecordType = newFieldMap.fieldsByRecordType;
              if (newFieldMap.isCompatibleWithField(fieldsByRecordType[nameToMatch(name as string)])) {
                newFieldMap.setField(fieldsByRecordType[nameToMatch(name as string)]);
              }
              if (data.fieldMappings !== undefined && Object.hasOwn(data.fieldMappings, originalColumnName)) {
                newFieldMap.field = Fields[data.fieldMappings[originalColumnName]];
                newFieldMap.selected = true;
              }
              return newFieldMap;
            });
          });
        } else if (this.isInstrumentsImport) {
          if (!(Boolean(data?.fieldNameForColumnName) && typeof data.fieldNameForColumnName === "object"))
            throw new Error("Field name for column name mapping data missing.");

          runInAction(() => {
            this.instrumentTemplateInfo = data.templateInfo as InstrumentTemplateAttrs;
            this.fieldNameForColumnName = data.fieldNameForColumnName as Record<string, string>;

            this.instrumentsMappings = (data.templateInfo as InstrumentTemplateAttrs).fields.map(
              ({ name, type }): ColumnFieldMap => {
                const originalColumnName = Object.keys(data.fieldNameForColumnName as Record<string, string>).find(
                  (key) => this.fieldNameForColumnName[key] === name,
                ) as string;

                const newFieldMap = new ColumnFieldMap({
                  recordType: this.recordType,
                  selected: true,
                  columnName: originalColumnName,
                  field: Fields.custom,
                  fieldName: name as string,
                  fieldType: apiStringToFieldType(type),
                  quantityUnitId: null,
                  options: (data.radioOptionsForColumn as Exclude<undefined, typeof data.radioOptionsForColumn>)?.[
                    originalColumnName
                  ],
                  fieldChangeCallback: fieldChangeHandler,
                  isNameUnique: (c) => this.isNameUnique(c),
                  columnsWithoutBlankValue: data.columnsWithoutBlankValue,
                });

                // after creation: auto-select conversion in suitable cases
                const fieldsByRecordType = newFieldMap.fieldsByRecordType;
                if (newFieldMap.isCompatibleWithField(fieldsByRecordType[nameToMatch(name as string)])) {
                  newFieldMap.setField(fieldsByRecordType[nameToMatch(name as string)]);
                }
                if (data.fieldMappings !== undefined && Object.hasOwn(data.fieldMappings, originalColumnName)) {
                  newFieldMap.field = Fields[data.fieldMappings[originalColumnName]];
                  newFieldMap.selected = true;
                }
                return newFieldMap;
              },
            );
          });
        } else {
          // containers and subsamples
          const defaultType = "string";
          runInAction(() => {
            const mappings = data.columnNames.map((columnName) => {
              const newFieldMap = new ColumnFieldMap({
                recordType: this.recordType,
                selected: true,
                columnName,
                field: Fields.none,
                fieldName: columnName,
                fieldType: apiStringToFieldType(defaultType),
                quantityUnitId: null,
                options: null,
                fieldChangeCallback: fieldChangeHandler,
                isNameUnique: (c) => this.isNameUnique(c),
                columnsWithoutBlankValue: data.columnsWithoutBlankValue,
              });
              const fieldsByRecordType = newFieldMap.fieldsByRecordType;
              if (newFieldMap.isCompatibleWithField(fieldsByRecordType[nameToMatch(columnName)])) {
                newFieldMap.setField(fieldsByRecordType[nameToMatch(columnName)]);
              } else {
                newFieldMap.setField(Fields.none);
                newFieldMap.selected = false;
              }
              if (data.fieldMappings !== undefined && Object.hasOwn(data.fieldMappings, columnName)) {
                newFieldMap.field = Fields[data.fieldMappings[columnName]];
                newFieldMap.selected = true;
              }
              return newFieldMap;
            });
            if (this.isContainersImport) {
              this.containersMappings = mappings;
            } else {
              this.subSamplesMappings = mappings;
            }
          });
        }
        state.transitionTo("parsed");
        // update state following name auto-mapping
        if (this.nameFieldIsSelected) state.transitionTo("nameSelected");
      }
    } catch (error) {
      console.error("parsing failed", error);
      this.state.transitionTo("parsingFailed", () => ({
        fileErrorMessage: Parsers.objectPath(["response", "data", "message"], error)
          .flatMap(Parsers.isString)
          .orElse(i18n.t("inventory:errors.unknownReason")),
      }));
      this.resetAllMappings();
    }
  }

  get fileErrorMessage(): string {
    this.state.assertCurrentState("parsingFailed");
    return (this.state.data as { fileErrorMessage: string }).fileErrorMessage;
  }

  fieldIsChosen(field: Field): boolean {
    return this.mappingsByRecordType.some((m) => m.field === field);
  }

  transformTemplateInfoForSubmission():
    | {
        fields: Array<unknown>;
        name: string;
      }
    | { id: number } {
    // biome-ignore lint/style/noNonNullAssertion: template is set whenever createNewTemplate is false
    if (!this.createNewTemplate) return pick(this.template!, ["id"]) as { id: number };
    if (!this.templateInfo) throw new Error("TemplateInfo is null");
    const templateFieldWithMappings: Array<{
      field: TemplateField;
      mapping: ColumnFieldMap;
    }> = zipWith(
      this.templateInfo.fields,
      this.templateInfo.fields
        .map(({ name }) => this.samplesMappings.find((f) => f.fieldName === name) ?? null)
        .filter(isNotNil),
      (f, m) => ({ field: f, mapping: m }),
    );
    const processedFields = templateFieldWithMappings
      .filter(({ mapping: { selected } }) => selected)
      .filter(({ mapping: { field } }) => field === Fields.custom)
      .map(({ field, mapping: { fieldName, chosenFieldType, options } }) => ({
        ...field,
        name: fieldName,
        type: fieldTypeToApiString(chosenFieldType),
        definition:
          chosenFieldType === FieldTypes.radio || chosenFieldType === FieldTypes.choice
            ? {
                options,
                multiple: false,
              }
            : null,
      }));
    const newUnitId = this.samplesMappings.find(
      ({ selected, field }) => field === Fields.quantity && selected,
    )?.quantityUnitId;
    if (this.quantityFieldIsSelected && newUnitId) {
      this.setDefaultUnitId(newUnitId);
    }

    return {
      ...this.templateInfo,
      fields: processedFields,
      name: this.templateName,
    };
  }

  transformInstrumentTemplateInfoForSubmission(): { fields: Array<unknown>; name: string } {
    if (!this.instrumentTemplateInfo) throw new Error("InstrumentTemplateInfo is null");
    const templateFieldWithMappings: Array<{
      field: { name: string; type: string; [key: string]: unknown };
      mapping: ColumnFieldMap;
    }> = zipWith(
      this.instrumentTemplateInfo.fields,
      this.instrumentTemplateInfo.fields
        .map(({ name }) => this.instrumentsMappings.find((f) => f.fieldName === name) ?? null)
        .filter(isNotNil),
      (f, m) => ({ field: f, mapping: m }),
    );
    const processedFields = templateFieldWithMappings
      .filter(({ mapping: { selected } }) => selected)
      .filter(({ mapping: { field } }) => field === Fields.custom)
      .map(({ field, mapping: { fieldName, chosenFieldType, options } }) => ({
        ...field,
        name: fieldName,
        type: fieldTypeToApiString(chosenFieldType),
        definition:
          chosenFieldType === FieldTypes.radio || chosenFieldType === FieldTypes.choice
            ? { options, multiple: false }
            : null,
      }));
    return {
      ...this.instrumentTemplateInfo,
      fields: processedFields,
      name: this.instrumentTemplateName,
    };
  }

  findField(mappings: Array<ColumnFieldMap>, field: Field): ColumnFieldMap | undefined {
    return mappings.find((f) => f.field === field);
  }

  findParsedColumnName(mappings: Array<ColumnFieldMap>, field: Field): string | undefined | null {
    const columnName = this.findField(mappings, field)?.columnName;
    return columnName ?? null;
  }

  makeMappingsObject(mappings: Array<ColumnFieldMap>): {
    [columnName: string]: string | null;
  } {
    const name = this.findParsedColumnName(mappings, Fields.name);
    if (!name) throw new Error("Name is a required field");
    const desc = this.findParsedColumnName(mappings, Fields.description);
    const eDate = this.findParsedColumnName(mappings, Fields.expiry_date);
    const tags = this.findParsedColumnName(mappings, Fields.tags);
    const source = this.findParsedColumnName(mappings, Fields.source);
    const quantity = this.findParsedColumnName(mappings, Fields.quantity);
    const importId = this.findParsedColumnName(mappings, Fields.import_identifier);
    const pContainerGlobalId = this.findParsedColumnName(mappings, Fields.parent_container_global_id);
    const pContainerImportId = this.findParsedColumnName(mappings, Fields.parent_container_import_id);
    const pSampleGlobalId = this.findParsedColumnName(mappings, Fields.parent_sample_global_id);
    const pSampleImportId = this.findParsedColumnName(mappings, Fields.parent_sample_import_id);
    const identifierMapping = this.findParsedColumnName(mappings, Fields.identifier);

    const result = {
      [name]: "name",
      ...(desc ? { [desc]: "description" } : {}),
      ...(eDate ? { [eDate]: "expiry date" } : {}),
      ...(tags ? { [tags]: "tags" } : {}),
      ...(source ? { [source]: "source" } : {}),
      ...(quantity ? { [quantity]: "quantity" } : {}),
      ...(importId ? { [importId]: "import identifier" } : {}),
      ...(pContainerGlobalId ? { [pContainerGlobalId]: "parent container global id" } : {}),
      ...(pContainerImportId ? { [pContainerImportId]: "parent container import id" } : {}),
      ...(pSampleGlobalId ? { [pSampleGlobalId]: "parent sample global id" } : {}),
      ...(pSampleImportId ? { [pSampleImportId]: "parent sample import id" } : {}),
      ...(identifierMapping ? { [identifierMapping]: "identifier" } : {}),

      ...Object.fromEntries(mappings.filter(({ selected }) => !selected).map(({ columnName }) => [columnName, null])),
    };
    return result;
  }

  async importFiles() {
    // Snapshot observable state before any mutations so that file appends and
    // the importSettings JSON always reflect the same evaluation.
    const containersFile = this.containersFile;
    const containersSubmittable = this.containersSubmittable;
    const samplesFile = this.samplesFile;
    const samplesSubmittable = this.samplesSubmittable;
    const subSamplesFile = this.subSamplesFile;
    const subSamplesSubmittable = this.subSamplesSubmittable;
    const instrumentsFile = this.instrumentsFile;
    const instrumentsSubmittable = this.instrumentsSubmittable;
    type InstrumentTemplateSnapshot = { createNew: true } | { createNew: false; template: InstrumentTemplateModel };
    const instrumentCreateNewTemplate = this.instrumentCreateNewTemplate;
    const instrumentTemplate = this.instrumentTemplate;
    const instrumentTemplateSnapshot: InstrumentTemplateSnapshot =
      !instrumentCreateNewTemplate && instrumentTemplate !== null
        ? { createNew: false, template: instrumentTemplate }
        : { createNew: true };

    this.state.transitionTo("submitting");
    const { peopleStore, uiStore } = getRootStore();

    try {
      const params = new FormData();

      if (containersFile && containersSubmittable) params.append("containersFile", containersFile);
      if (samplesFile && samplesSubmittable) params.append("samplesFile", samplesFile);
      if (subSamplesFile && subSamplesSubmittable) params.append("subSamplesFile", subSamplesFile);
      if (instrumentsFile && instrumentsSubmittable) params.append("instrumentsFile", instrumentsFile);

      params.append(
        "importSettings",
        // make different fieldMappings object (per type)
        JSON.stringify({
          containerSettings: containersSubmittable
            ? {
                fieldMappings: this.makeMappingsObject(this.containersMappings),
              }
            : null,
          sampleSettings: samplesSubmittable
            ? {
                templateInfo: this.transformTemplateInfoForSubmission(),
                fieldMappings: this.makeMappingsObject(this.samplesMappings),
              }
            : null,
          subSampleSettings: subSamplesSubmittable
            ? {
                fieldMappings: this.makeMappingsObject(this.subSamplesMappings),
              }
            : null,
          instrumentSettings: instrumentsSubmittable
            ? {
                templateId: instrumentTemplateSnapshot.createNew ? null : instrumentTemplateSnapshot.template.id,
                templateInfo: instrumentTemplateSnapshot.createNew
                  ? this.transformInstrumentTemplateInfoForSubmission()
                  : null,
                fieldMappings: this.makeMappingsObject(this.instrumentsMappings),
              }
            : null,
        }),
      );

      type ImportResults = {
        type: string;
        status: string;
        results: ReadonlyArray<{
          error: { errors: ReadonlyArray<string> } | null;
          record: Record<string, unknown> & { globalId: GlobalId };
        }>;
        templateResult: {
          error: { errors: ReadonlyArray<string> };
          record: Record<string, unknown> & { globalId: GlobalId };
        };
      };

      const {
        // renaming status property to prevent duplication.
        data: { status: importStatus, sampleResults, containerResults, subSampleResults, instrumentResults },
      } = await showToastWhilstPending(
        i18n.t("inventory:import.records.importing"),
        ApiService.post<{
          status: string;
          sampleResults: ImportResults;
          containerResults: ImportResults;
          subSampleResults: ImportResults;
          instrumentResults: ImportResults;
        }>("/import/importFiles", params),
      );

      // multiple results can be returned and should be handled per type
      const resultsGroups = [containerResults, sampleResults, subSampleResults, instrumentResults].filter((rg) => rg);

      resultsGroups.forEach((group) => {
        const { results, type, status, templateResult } = group;
        const labelByResultsType = importResultTypeLabel(type);

        if (status !== "COMPLETED") {
          this.state.transitionTo("nameSelected");
          if (type === "SAMPLE") results.concat(templateResult);
          uiStore.addAlert(
            mkAlert({
              message: i18n.t("inventory:import.records.invalidData", { type: labelByResultsType }),
              variant: "error",
              details: results
                .map(({ error }, i) => [error, i] as [{ errors: ReadonlyArray<string> }, number])
                .filter(([error]) => error)
                .flatMap(([error, i]) =>
                  error.errors.map((e) => ({
                    title: i18n.t("inventory:import.records.row", { row: i + 1 }),
                    help: e,
                    variant: "error" as const,
                  })),
                ),
              retryFunction: () => this.importFiles(),
            }),
          );
        } else {
          uiStore.unsetDirty();
          // in group PREVALIDATED case, don't create records and alerts
          const factory = new MemoisedFactory();
          uiStore.addAlert(
            mkAlert({
              message: i18n.t("inventory:import.records.success", { type: labelByResultsType }),
              variant: "success",
              isInfinite: true,
              details: results
                .map(({ record }) => {
                  const newRecord = factory.newRecord(record);
                  newRecord.populateFromJson(factory, record, null);
                  return newRecord;
                })
                .map((record) => ({
                  title: i18n.t("inventory:import.records.importedRecord", { name: record.name }),
                  variant: "success",
                  record,
                })),
            }),
          );
        }
      });
      if (importStatus === "COMPLETED") {
        this.state.transitionTo("successfullySubmitted");
        if (peopleStore.currentUser) void peopleStore.currentUser.getBench();
        this.resetAllLoadedFiles();
        this.resetAllMappings();
        this.setTemplateName(i18n.t("inventory:import.fields.defaultTemplateName"));
        this.setInstrumentTemplateName(i18n.t("inventory:import.fields.defaultTemplateName"));
      }
    } catch (error) {
      const gatewayTimeout =
        Parsers.objectPath(["response", "status"], error).flatMap(Parsers.isNumber).orElse(-1) === 504;
      this.state.transitionTo("nameSelected");
      uiStore.addAlert(
        mkAlert({
          title: gatewayTimeout
            ? i18n.t("inventory:import.submit.mayHaveCompleted")
            : i18n.t("inventory:import.submit.recordsNotImported"),
          message: getErrorMessage(error, i18n.t("inventory:errors.unknownReason")),
          variant: gatewayTimeout ? "warning" : "error",
        }),
      );
      console.error("Problem on import.", error);
    }
  }

  isNameUnique(columnFieldMap: ColumnFieldMap): boolean {
    const mappingsByType: Record<ImportRecordType, Array<ColumnFieldMap>> = {
      CONTAINERS: this.containersMappings,
      SAMPLES: this.samplesMappings,
      SUBSAMPLES: this.subSamplesMappings,
      INSTRUMENTS: this.instrumentsMappings,
    };
    const mappings = new RsSet(mappingsByType[columnFieldMap.recordType]);
    mappings.delete(columnFieldMap);
    return !mappings.map((m) => m.fieldName.trim()).has(columnFieldMap.fieldName.trim());
  }

  /*
   * When importing samples into an existing template (this.template), the
   * columns of the CSV file must match the fields of the template. Once the
   * built-in fields have been assigned (name, description, etc.), the
   * remaining selected fields (the mappings with a .field of Fields.custom and
   * are selected) should be the same in number as the CSV columns, have the
   * same name, and have compatible types. If any of these checks fails then
   * this computed returns false. It returns null if the user is creating a new
   * template.
   */
  get importMatchesExistingTemplate(): { matches: false; reason: string } | { matches: true } | null {
    if (this.createNewTemplate) return null;
    if (!this.template) return { matches: false, reason: i18n.t("inventory:import.columnMapping.templateNotSelected") };
    const template = this.template;

    const customFields = this.samplesMappings.filter(({ field, selected }) => field === Fields.custom && selected);
    if (customFields.length !== template.fields.length) {
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateCustomColumnCountMismatch", {
          customCount: customFields.length,
          templateCount: template.fields.length,
        }),
      };
    }

    const namePairs = zipWith(customFields, template.fields, ({ columnName }, { name: fieldName }) => [
      columnName,
      fieldName,
    ]);
    if (namePairs.some(([c, f]) => c !== f)) {
      const help = formatList(
        namePairs
          .filter(([c, f]) => c !== f)
          .map(([c, f]) =>
            i18n.t("inventory:import.columnMapping.templateColumnNameMismatchItem", {
              csvColumnName: c,
              templateFieldName: f,
            }),
          ),
        i18n.resolvedLanguage ?? i18n.language,
      );
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateColumnNamesMismatch", { mismatches: help }),
      };
    }

    const notMatchingByType = new Set(
      zipWith(customFields, template.fields, ({ allValidTypes, columnName }, { type: fieldType }) =>
        allValidTypes.includes(apiStringToFieldType(fieldType.toLowerCase())) ? null : columnName,
      ),
    );
    notMatchingByType.delete(null);
    if (notMatchingByType.size > 0) {
      const help = formatList(
        [...notMatchingByType].filter((columnName): columnName is string => columnName !== null),
        i18n.resolvedLanguage ?? i18n.language,
      );
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateColumnTypeMismatch", { columns: help }),
      };
    }
    const columnsWithMissingData = zipWith(
      customFields,
      template.fields,
      ({ columnsWithoutBlankValue, columnName }, { mandatory }) =>
        mandatory && !columnsWithoutBlankValue.includes(columnName) ? columnName : null,
    ).filter((columnName): columnName is string => columnName !== null);
    if (columnsWithMissingData.length) {
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateRequiredValueMissing", {
          columns: formatList(columnsWithMissingData, i18n.resolvedLanguage ?? i18n.language),
        }),
      };
    }

    return { matches: true };
  }

  get importInstrumentMatchesExistingTemplate(): { matches: false; reason: string } | { matches: true } | null {
    if (this.instrumentCreateNewTemplate) return null;
    if (!this.instrumentTemplate)
      return { matches: false, reason: i18n.t("inventory:import.columnMapping.templateNotSelected") };
    const template = this.instrumentTemplate;

    const customFields = this.instrumentsMappings.filter(({ field, selected }) => field === Fields.custom && selected);
    if (customFields.length !== template.fields.length) {
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateCustomColumnCountMismatch", {
          customCount: customFields.length,
          templateCount: template.fields.length,
        }),
      };
    }

    const namePairs = zipWith(customFields, template.fields, ({ columnName }, { name: fieldName }) => [
      columnName,
      fieldName,
    ]);
    if (namePairs.some(([c, f]) => c !== f)) {
      const help = formatList(
        namePairs
          .filter(([c, f]) => c !== f)
          .map(([c, f]) =>
            i18n.t("inventory:import.columnMapping.templateColumnNameMismatchItem", {
              csvColumnName: c,
              templateFieldName: f,
            }),
          ),
        i18n.resolvedLanguage ?? i18n.language,
      );
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateColumnNamesMismatch", { mismatches: help }),
      };
    }

    const notMatchingByType = new Set(
      zipWith(customFields, template.fields, ({ allValidTypes, columnName }, { type: fieldType }) =>
        allValidTypes.includes(apiStringToFieldType(fieldType.toLowerCase())) ? null : columnName,
      ),
    );
    notMatchingByType.delete(null);
    if (notMatchingByType.size > 0) {
      const help = formatList(
        [...notMatchingByType].filter((columnName): columnName is string => columnName !== null),
        i18n.resolvedLanguage ?? i18n.language,
      );
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateColumnTypeMismatch", { columns: help }),
      };
    }

    const columnsWithMissingData = zipWith(
      customFields,
      template.fields,
      ({ columnsWithoutBlankValue, columnName }, { mandatory }) =>
        mandatory && !columnsWithoutBlankValue.includes(columnName) ? columnName : null,
    ).filter((columnName): columnName is string => columnName !== null);
    if (columnsWithMissingData.length) {
      return {
        matches: false,
        reason: i18n.t("inventory:import.columnMapping.templateRequiredValueMissing", {
          columns: formatList(columnsWithMissingData, i18n.resolvedLanguage ?? i18n.language),
        }),
      };
    }

    return { matches: true };
  }

  // group all byType computeds
  byRecordType(prop: "label" | "fileLoaded" | "file" | "mappings" | "resetMappings"): unknown {
    const byType = {
      label: this.labelByRecordType,
      fileLoaded: this.fileByRecordTypeLoaded,
      file: this.fileByRecordType,
      mappings: this.mappingsByRecordType,
      resetMappings: () => this.resetMappingsByRecordType(),
    };
    return byType[prop];
  }

  /*
   * Whenever the URL changes, this method should be called to update the
   * current type of record being imported of the current instance of this
   * class.
   *
   * @throws If `recordType` is either missing from the passed URLSearchParams
   *         or is an invalid string
   */
  updateRecordType(urlSearchParams: URLSearchParams): void {
    const recordTypeParam = urlSearchParams.get("recordType");
    if (!recordTypeParam) throw new Error("recordType URL arg is missing but required");

    const recordType: ImportRecordType = Result.first(
      parseString("SAMPLES", recordTypeParam),
      parseString("CONTAINERS", recordTypeParam),
      parseString("SUBSAMPLES", recordTypeParam),
      parseString("INSTRUMENTS", recordTypeParam),
    ).orElseGet(() => {
      throw new Error(`Could not parse URL arg, invalid value: "${recordTypeParam}".`);
    });

    this.setCurrentRecordType(recordType);
  }
}
